import {useState, useEffect, useMemo } from 'react';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { Reply as ReplyIcon, Phone, Link2 } from 'lucide-react';
import './WhatsappSenderTemplatePreview.scss';

export default function WhatsappSenderTemplatePreview(props){

    const { templateList } = useReduxLeads();
    const [whatsappTemplate, setWhatsappTemplate] = useState();
    const [template, setTemplate] = useState({});
    const className = 'whatsappSenderTemplatePreview';

    useEffect(()=>{
        renderData();
    },[]);

    const renderData = async()=> {
        if(!!props.template) {
            let template = props.template.template;
            let findCurrentTemplate = templateList.find((item)=> item.name === template.name);
            setTemplate(findCurrentTemplate);
            setWhatsappTemplate(template);
        }
    }

    const getBody = (components, example) => {
        if(!!components) {
            const body = components.find(c => c.type === 'BODY');
            const parameters = whatsappTemplate.components.find(c => c.type === 'body')?.parameters;
            if (!body) return '';
            let text = body.text;
            if (!!parameters && parameters.length > 1) {
                parameters.forEach((val) => {
                  text = text.replace(/{{[^}]+}}/, (val?.text ?? ''));
                });
            }
            return text;
        }
    };

    return (
        <div className={"whatsappSenderTemplatePreview whatsappSenderTemplatePreview" + (!!props.hideBackground ? '' : '-container')}>
            {props?.showBody ?
                <div className="whatsappSenderTemplatePreview-body">
                    <WhatsappText text={getBody(template?.components)} />
                </div>
            :
                <div className="whatsappSenderTemplatePreview-card">
                    <div className="whatsappSenderTemplatePreview-header">
                        {getHeader(template?.components, whatsappTemplate?.components?.find(c => c.type === 'header')?.parameters)}
                    </div>
                    <div className="whatsappSenderTemplatePreview-body">
                        <WhatsappText text={getBody(template?.components)} />
                    </div>
                    {getFooter(template?.components)}
                    {getButtons(template?.components)}
                </div>
            }
        </div>
    );
}

const WhatsappText = ({ text = "" }) => {
    const WORD_LIMIT = 20;
    const [expanded, setExpanded] = useState(false);
    const words = useMemo(
        () => text.trim().split(/\s+/).filter(Boolean),
        [text]
    );
    const shouldTruncate = words.length > WORD_LIMIT;
    const displayText = useMemo(() => {
        if (expanded || !shouldTruncate) {
            return text;
        }
        let wordCount = 0;
        const truncated = text
            .split(/(\s+)/)
            .filter((part) => {
                if (/\s+/.test(part)) {
                    return true;
                }
                wordCount++;
                return wordCount <= WORD_LIMIT;
            })
            .join("")
            .trimEnd();
        return `${truncated}...`;
    }, [text, expanded, shouldTruncate]);

    const TOKEN_REGEX = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`|\n)/g;
    const tokens = displayText.split(TOKEN_REGEX).filter(Boolean);

    return (
        <span className="whatsapp-text">
            {tokens.map((token, i) => {
                if (token === "\n") {
                    return <br key={i} />;
                }
                if (token.startsWith("*") && token.endsWith("*")) {
                    return <strong key={i}>{token.slice(1, -1)}</strong>;
                }
                if (token.startsWith("_") && token.endsWith("_")) {
                    return <em key={i}>{token.slice(1, -1)}</em>;
                }
                if (token.startsWith("~") && token.endsWith("~")) {
                    return (
                        <span
                            key={i}
                            className="whatsapp-text__strike"
                        >
                            {token.slice(1, -1)}
                        </span>
                    );
                }
                if (token.startsWith("`") && token.endsWith("`")) {
                    return (
                        <code key={i} className="whatsapp-text__code">
                            {token.slice(1, -1)}
                        </code>
                    );
                }
                return <span key={i}>{token}</span>;
            })}
            {shouldTruncate && (
                <button
                    type="button"
                    className="whatsapp-text__toggle"
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded ? "Read less" : "Read more"}
                </button>
            )}
        </span>
    );
};

const getHeader = (components = [], parameters = []) => {
    const header = components.find(c => c.type === "HEADER");
    if (!header) return <div/>;
    if(!!parameters) {
        let firstHeader = parameters[0];
        if(!!firstHeader) {
            let type = firstHeader.type;
            if(type === 'text') {
                let headerText = header.text;
                headerText = headerText.replace(/{{[^}]+}}/, (firstHeader.text ?? ''));
                return (
                    <div className="whatsappSenderTemplatePreview-header-text">
                        {headerText}
                    </div>
                );
            }
            if(type === 'image') {
                let src = firstHeader.image.link;
                return (
                    <div className="whatsappSenderTemplatePreview-header-media">
                        <img src={src} alt="header" />
                    </div>
                );
            }
            if(type === 'video') {
                let src = firstHeader.video.link;
                return (
                    <div className="whatsappSenderTemplatePreview-header-media">
                        <video src={src} controls />
                    </div>
                );
            }
        }
    }
    if (header.format === "TEXT") {
        return (
            <div className="whatsappSenderTemplatePreview-header-text">
                {header.text}
            </div>
        );
    }
    if (["IMAGE", "VIDEO"].includes(header.format)) {
        const src = header.example?.header_handle?.[0];
        if (!src) return <div/>;

        return (
            <div className="whatsappSenderTemplatePreview-header-media">
                {header.format === "IMAGE" ? (
                    <img src={src} alt="header" />
                ) : (
                    <video src={src} controls />
                )}
            </div>
        );
    }

    return <div/>;
};

const getFooter = (components = []) => {
    const footer = components.find(c => c.type === "FOOTER");
    if (!footer?.text) return <div/>;

    return (
        <div className="whatsappSenderTemplatePreview-footer">
            {footer.text}
        </div>
    );
};

const getButtons = (components = []) => {
    const btns = components.find(c => c.type === "BUTTONS");
    if (!btns?.buttons?.length) return <div/>;
    return (
        <div className="whatsappSenderTemplatePreview-buttons">
            {btns.buttons.map((b, i) => (
                b.type === 'URL' ?
                    <a href={b.url} target='_blank' key={i}>
                        <button
                            className="whatsappSenderTemplatePreview-button">
                                <Link2 size={16}/>
                                {b.text}
                         </button>
                     </a>
                :
                    <button
                        key={i}
                        className="whatsappSenderTemplatePreview-button wc-ic-btn">
                            {b.type === 'QUICK_REPLY' && <ReplyIcon size={16}/>}
                            {b.type === 'PHONE_NUMBER' && <Phone size={14}/>}
                            {b.text}
                    </button>
            ))}
        </div>
    );
};
